import React from 'react';
import Button from '../Button';
import ArrowUpIcon from '../CustomIcons/ArrowUpIcon';
import DisabledIcon from '../CustomIcons/DisabledIcon';
import { TextArea } from '../TextArea';

export interface ChatComposerProps {
    value: string;
    onChange: (value: string) => void;
    onSend: () => void;
    /** Offered in place of send while `busy`. Without it, send is simply disabled. */
    onStop?: () => void;
    /** A reply is streaming. */
    busy?: boolean;
    disabled?: boolean;
    placeholder?: string;
    maxLength?: number;
    rows?: number;
    /**
     * How many lines the field grows to before it scrolls internally instead of growing
     * further. Defaults well above `rows` so the field grows by default rather than staying
     * fixed at its starting height — a consumer that genuinely wants a static box passes
     * `maxRows` equal to `rows`.
     */
    maxRows?: number;
    /**
     * `'in-field'` (the default, so no existing consumer moves) nests a round send button
     * inside the field, trailing the text. `'beside'` matches the policy design instead: a
     * plain field with a labelled `Send` button to its right. Either way the field stays a
     * `TextArea` — the mock draws a single-line input, but a composer that cannot take a
     * second line is a downgrade, and `TextArea` already owns the Enter-to-send convention.
     */
    sendPlacement?: 'in-field' | 'beside';
    /**
     * `'assistant'` marks the field itself as AI-owned chrome: its focus border recolours to
     * the assistant violet instead of the default accent. Defaults to `'default'` — an AI
     * surface (e.g. the playground) opts in deliberately rather than inheriting it silently.
     */
    tone?: 'default' | 'assistant';
    /** Controls belonging with the composer — a model picker, a token meter. */
    children?: React.ReactNode;
    'aria-label'?: string;
    className?: string;
}

/**
 * The message box. Owns the send affordance and the Enter/Shift+Enter convention so every
 * chat surface submits the same way.
 *
 * `shrink-0` keeps it at its natural height inside a `Chat`, so a growing transcript takes
 * the remaining space instead of squeezing the box a reader is typing into.
 */
const ChatComposer: React.FC<ChatComposerProps> = ({
    value,
    onChange,
    onSend,
    onStop,
    busy = false,
    disabled = false,
    placeholder = 'Ask anything',
    maxLength,
    rows = 2,
    maxRows = 6,
    sendPlacement = 'in-field',
    tone = 'default',
    children,
    'aria-label': ariaLabel = 'Message',
    className = '',
}) => {
    const sendable = !disabled && !busy && value.trim().length > 0;
    // Kept as a handler rather than a plain boolean so TypeScript narrows `onStop` to
    // defined wherever this is truthy, instead of requiring a non-null assertion below.
    const stopHandler = busy ? onStop : undefined;

    const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();

            if (sendable) {
                onSend();
            }
        }
    };

    const field = (
        <TextArea
            name="chat-composer"
            aria-label={ariaLabel}
            placeholder={placeholder}
            value={value}
            onChange={onChange}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            rows={rows}
            maxRows={maxRows}
            maxLength={maxLength}
            resize="none"
            size="small"
            tone={tone}
            inFieldAction={
                sendPlacement !== 'in-field' ? undefined : stopHandler ? (
                    // Round, and labelled only for assistive tech and on hover: the field it
                    // sits in is the whole context a send button needs.
                    <Button
                        variant="outline"
                        size="small"
                        iconOnly
                        radius="xl"
                        icon={<DisabledIcon size={13} />}
                        tooltip="Stop"
                        aria-label="Stop"
                        onClick={stopHandler}
                    />
                ) : (
                    <Button
                        variant="filled"
                        size="small"
                        iconOnly
                        radius="xl"
                        disabled={!sendable}
                        icon={<ArrowUpIcon size={12} />}
                        tooltip="Send"
                        aria-label="Send"
                        onClick={onSend}
                    />
                )
            }
        />
    );

    return (
        <div
            className={`flex shrink-0 flex-col p-4 ${className}`}
            // Opaque and shadowed rather than bordered, mirroring `Chat.Header`: pinned to
            // the foot of a transcript that scrolls behind it, the composer has to hide what
            // passes under it, and a drawn line reads as a seam where a shadow does not.
            style={{
                backgroundColor: 'var(--malachite-card-background)',
                boxShadow: '0 -4px 6px -4px var(--malachite-card-shadow)',
            }}
        >
            {sendPlacement === 'beside' ? (
                <div className="flex items-center gap-2">
                    <div className="min-w-0 flex-1">{field}</div>
                    {/* `large` (42px) matches the field's own height; the smaller tiers
                        (28px, 32px) leave the button visibly short beside it. */}
                    {stopHandler ? (
                        <Button variant="outline" size="large" onClick={stopHandler}>
                            Stop
                        </Button>
                    ) : (
                        <Button variant="filled" size="large" disabled={!sendable} onClick={onSend}>
                            Send
                        </Button>
                    )}
                </div>
            ) : (
                field
            )}
            {children ? (
                // Flush against the field and to its trailing edge, inset by the field's own
                // corner radius so no tab sits under the curve, and wrapping rather than
                // crushing its contents when the column is narrow.
                <div className="flex flex-wrap items-start justify-end gap-2 px-[var(--malachite-radius-xl,12px)]">
                    {children}
                </div>
            ) : null}
        </div>
    );
};

export default ChatComposer;
export { ChatComposer };
